import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Pagination from '@mui/material/Pagination'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ResultCard from './ResultCard'
import NotFound from './NotFound'
import { deriveMatchQuality, sortByMatchQuality } from './matchQuality'
import { ISearchResult } from 'types/searchInterfaces'
import { formatDistanceToNow } from 'date-fns'
import { enGB, fi } from 'date-fns/locale'
import i18next from 'i18next'
import { useCountryData } from 'hooks'
import { combinedSearchApi } from 'api/CombinedSearchAPI'
import { IError } from 'types/error'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { PersonId } from 'types/ids'
import { IPerson } from 'types/userInterfaces'
import EditButton from 'Components/reusable/IconButtons/EditButton'
import { convertToTokens } from '../tokenUtils'
import { hydrateSearchResults, mapCombinedCandidateToSearchResult } from '../combinedSearchMapping'

/**
 * React functional component for displaying search results.
 */
const Results: React.FC = () => {
  const { t } = useTranslation()
  const { searchData, setSearchData } = useSearchData()
  const { countries } = useCountryData()

  const [results, setResults] = useState<ISearchResult[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [resultsCount, setResultsCount] = useState<number>(10)
  const [backendError, setBackendError] = useState<IError>()
  const [proposeCard, setProposeCard] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [isEditingQuery, setIsEditingQuery] = useState<boolean>(false)
  const [editedQuery, setEditedQuery] = useState<string>('')
  const [editQueryLoading, setEditQueryLoading] = useState<boolean>(false)

  useEffect(() => {
    const controller = new AbortController()

    if (searchData.results) {
      const results = searchData.results
      ;(async () => {
        try {
          setLoading(true)
          setCurrentPage(1)

          // Fetch full data only for the first 10 results
          const firstPageResults = results.slice(0, 10)
          const fullData = await hydrateSearchResults(firstPageResults, controller)

          if (
            fullData.length < 10 &&
            fullData.length > 0 &&
            firstPageResults.every(result => result.kind !== 'freeText')
          ) {
            fullData.push({ kind: 'partial', anonymous: true, personId: 0 as PersonId, Person: {} as IPerson })
            setProposeCard(true)
          } else {
            setProposeCard(false)
          }

          setResults(fullData)
          setLoading(false)
        } catch (error) {
          setBackendError(error as IError)
          setLoading(false)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [searchData.results])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  const localeMap = {
    en: enGB,
    fi: fi,
  }

  const fetched = `${
    searchData.lastFetched
      ? formatDistanceToNow(searchData.lastFetched, { locale: localeMap[i18next.language as keyof typeof localeMap] })
      : null
  }`

  const changePage = async (_e: ChangeEvent<unknown>, newPage: number) => {
    if (searchData.results && searchData.results.length > 0) {
      try {
        setLoading(true)
        setProposeCard(false)
        setCurrentPage(newPage)

        // Get lightweight results for the current page
        const pageResults = searchData.results.slice((newPage - 1) * 10, newPage * 10)

        // Fetch full data only for these 10 results
        const fullData = await hydrateSearchResults(pageResults)

        if (fullData.length < 10 && fullData.length > 0 && pageResults.every(result => result.kind !== 'freeText')) {
          fullData.push({ kind: 'partial', anonymous: true, personId: 0 as PersonId, Person: {} as IPerson })
          setProposeCard(true)
        }

        setResults(fullData)
        setResultsCount(newPage * 10)

        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        setBackendError(error as IError)
      } finally {
        setLoading(false)
      }
    }
  }

  /**
   * Starts inline editing of the AI-improved free text search query.
   */
  const startEditingQuery = () => {
    setEditedQuery(searchData.improvedQuery ?? '')
    setIsEditingQuery(true)
  }

  /**
   * Cancels inline editing of the free text search query without submitting.
   */
  const cancelEditingQuery = () => {
    setIsEditingQuery(false)
  }

  /**
   * Submits the user-edited free text search query, re-running the search without
   * triggering the AI query improvement again.
   */
  const submitEditedQuery = async () => {
    if (!('query' in searchData.terms) || editedQuery.trim().length === 0) {
      return
    }

    try {
      setEditQueryLoading(true)

      const networkIds = searchData.terms.networks.map(n => n.id)
      const response = await combinedSearchApi.search({
        networkIds,
        tokens: convertToTokens(searchData.terms),
        partial: true,
        freeText: editedQuery,
        skipQueryImprovement: true,
      })
      setSearchData({
        ...searchData,
        results: sortByMatchQuality(response.candidates.map(mapCombinedCandidateToSearchResult)),
        improvedQuery: response.improvedQuery,
        lastFetched: new Date(),
      })

      setIsEditingQuery(false)
    } catch (error) {
      setBackendError(error as IError)
    } finally {
      setEditQueryLoading(false)
    }
  }

  const hasStrongOrModerateMatch = Boolean(searchData.results?.some(result => deriveMatchQuality(result) !== 'weak'))
  const showNoStrongMatchesBanner =
    Boolean(searchData.improvedQuery) && (searchData.results?.length ?? 0) > 0 && !hasStrongOrModerateMatch

  let to
  if (results.length < 10 && searchData.results) {
    to = searchData.results?.length
  } else {
    to = resultsCount
  }

  return (
    <Grid mt={4} container spacing={2}>
      <Grid size={12}>
        <Grid container justifyContent="space-between">
          <Grid>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('search.results')}
            </Typography>
          </Grid>
          <Grid>
            <Typography gutterBottom>{t('search.lastFetched', { time: fetched })}</Typography>
          </Grid>
        </Grid>
        {searchData.results && (
          <Typography>
            {t('search.resultsCount', {
              from: (currentPage - 1) * 10 + 1,
              to: proposeCard && to > 0 ? to - 1 : to,
              of: searchData.results?.length,
            })}
          </Typography>
        )}
        {showNoStrongMatchesBanner && (
          <Grid size={12} mt={1}>
            <Alert severity="warning" variant="filled">
              {t('search.noStrongMatches')}
            </Alert>
          </Grid>
        )}
        {searchData.results && searchData.results.length > 0 && (
          <Grid container alignItems="center" spacing={1} mt={1}>
            <Grid>
              <Typography variant="body2" color="text.secondary">
                {t('search.chipLegend.title')}:
              </Typography>
            </Grid>
            <Grid>
              <Chip
                size="small"
                label={t('search.chipLegend.exactMatch')}
                color="primary"
                variant="filled"
                sx={{ mr: 0.5, borderRadius: 0.5 }}
              />
            </Grid>
            <Grid>
              <Chip
                size="small"
                label={t('search.chipLegend.similarMatch')}
                color="secondary"
                variant="filled"
                sx={{ mr: 0.5, borderRadius: 0.5 }}
              />
            </Grid>
            <Grid>
              <Chip
                size="small"
                label={t('search.chipLegend.other')}
                variant="outlined"
                sx={{ mr: 0.5, borderRadius: 0.5 }}
              />
            </Grid>
          </Grid>
        )}
        {searchData.improvedQuery && (
          <Grid size={12} mt={1}>
            {isEditingQuery ? (
              <Grid container alignItems="center" spacing={1}>
                <Grid size="grow">
                  <TextField
                    data-testid="input-search-edit-query"
                    fullWidth
                    size="small"
                    variant="standard"
                    value={editedQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setEditedQuery(e.target.value)}
                    disabled={editQueryLoading}
                    autoFocus
                  />
                </Grid>
                <Grid>
                  <Button
                    data-testid="button-search-edit-query-submit"
                    variant="contained"
                    size="small"
                    color="newPrimary"
                    onClick={submitEditedQuery}
                    disabled={editQueryLoading || editedQuery.trim().length === 0}
                  >
                    {t('search.submit')}
                  </Button>
                </Grid>
                <Grid>
                  <Button size="small" onClick={cancelEditingQuery} disabled={editQueryLoading}>
                    {t('cancel')}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container alignItems="center" spacing={0.5}>
                <Grid>
                  <Typography fontWeight="bold">
                    {t('search.searchedUsing', { query: searchData.improvedQuery })}
                  </Typography>
                </Grid>
                <Grid>
                  <EditButton clickAction={startEditingQuery} noFloat testId="button-search-edit-query" />
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      {searchData.results && (
        <Grid container justifyContent="center">
          <Pagination
            page={currentPage}
            count={Math.ceil(searchData.results.length / 10)}
            variant="outlined"
            shape="rounded"
            showFirstButton
            showLastButton
            siblingCount={2}
            onChange={changePage}
            color="primary"
          />
        </Grid>
      )}
      <Grid size={12}>
        {!loading &&
          results?.map(result => {
            if (result.kind === 'partial' && result.anonymous && result.personId === 0) {
              return <NotFound key={result.personId} />
            } else {
              return (
                <ResultCard
                  key={result.anonymousId ?? result.personId}
                  result={result}
                  terms={searchData.terms}
                  countries={countries}
                />
              )
            }
          })}
        {loading && (
          <Grid container justifyContent="center" sx={{ minHeight: '300px' }} alignContent="center">
            <CircularProgress size={60} />
          </Grid>
        )}
      </Grid>
      {searchData.results && (
        <Grid container justifyContent="center">
          <Pagination
            page={currentPage}
            count={Math.ceil(searchData.results.length / 10)}
            variant="outlined"
            shape="rounded"
            showFirstButton
            showLastButton
            siblingCount={2}
            onChange={changePage}
            color="primary"
          />
        </Grid>
      )}
    </Grid>
  )
}

export default Results
